import React, { useMemo } from 'react';
import { buildDashboardFromTemplate } from './myDashboardTemplates';

/**
 * A miniature of a starter dashboard: the real layout the template creates
 * (same 12-column grid positions), with a small drawing of each widget.
 */

const G = 'var(--tpl-up, #22c55e)';
const R = 'var(--tpl-down, #ef4444)';
const A = 'var(--tpl-accent, #818cf8)';
const L = 'var(--tpl-line, rgba(148, 163, 184, 0.45))';
const F = 'var(--tpl-faint, rgba(148, 163, 184, 0.18))';

const Line = ({ d, color = A }) => (
  <path className="tpl-draw" d={d} fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" pathLength="1" />
);

const EQUITY = 'M2,48 C14,46 18,40 28,42 C38,44 42,30 52,32 C62,34 66,22 76,24 C84,26 90,12 98,10';

function Glyph({ type }) {
  switch (type) {
    case 'myKpi':
    case 'kpi':
    case 'mkSymbol':
      return (
        <svg viewBox="0 0 100 60" preserveAspectRatio="none">
          <rect x="6" y="7" width="34" height="5" rx="2" fill={L} />
          <rect x="6" y="17" width="54" height="10" rx="3" fill="var(--tpl-strong, rgba(226, 232, 240, 0.85))" />
          <Line d="M6,52 C20,50 26,44 38,46 C50,48 56,38 68,40 C80,42 86,32 96,30" color={G} />
        </svg>
      );
    case 'myEquity':
    case 'weeklyChart':
    case 'myVsStrategy':
      return (
        <svg viewBox="0 0 100 60" preserveAspectRatio="none">
          <path d={`${EQUITY} L98,58 L2,58 Z`} fill={A} opacity="0.18" />
          <Line d={EQUITY} />
          {type === 'myVsStrategy' ? <Line d="M2,50 C20,48 30,44 46,40 C60,37 76,32 98,24" color={G} /> : null}
        </svg>
      );
    case 'twToday':
      return (
        <svg viewBox="0 0 100 20" preserveAspectRatio="none">
          {[0, 1, 2, 3, 4].map((i) => (
            <g key={i}>
              <rect x={2 + i * 19.6} y="2" width="18" height="16" rx="2" fill={i === 4 ? G : F} opacity={i === 4 ? 0.35 : 1} />
              <rect x={4 + i * 19.6} y="5" width="9" height="2.5" rx="1" fill={L} />
              <rect x={4 + i * 19.6} y="10" width={i === 0 ? 13 : 7} height="4" rx="1.5" fill={i === 0 ? G : 'var(--tpl-strong, rgba(226, 232, 240, 0.85))'} />
            </g>
          ))}
        </svg>
      );
    case 'twDailyPnl':
      return (
        <svg viewBox="0 0 100 60" preserveAspectRatio="none">
          {[...Array(16)].map((_, i) => {
            const v = [8, 12, -6, 14, 5, -10, 18, 7, -4, 10, 16, -8, 6, 20, -5, 11][i];
            return <rect key={i} x={4 + i * 6} y={v > 0 ? 34 - v : 34} width="4" height={Math.abs(v)} rx="1" fill={v > 0 ? G : R} opacity="0.85" />;
          })}
          <Line d="M4,40 C20,36 30,30 46,28 C60,26 76,18 98,12" />
        </svg>
      );
    case 'twAlerts':
      return (
        <svg viewBox="0 0 100 60" preserveAspectRatio="none">
          {[R, G, '#f59e0b', G].map((c, i) => (
            <g key={i}>
              <rect x="4" y={5 + i * 13.5} width="8" height="8" rx="2" fill={c} opacity="0.75" />
              <rect x="16" y={6 + i * 13.5} width={40 - (i % 2) * 10} height="3.5" rx="1.5" fill={L} />
              <rect x="16" y={11 + i * 13.5} width={28 - (i % 3) * 5} height="2.5" rx="1" fill={F} />
              <rect x="84" y={7 + i * 13.5} width="12" height="3" rx="1.5" fill={F} />
            </g>
          ))}
        </svg>
      );
    case 'myPnlCalendar':
    case 'twCalendar':
    case 'calendar': {
      const cells = [];
      const pattern = 'gggrg.rggr.gggrrg.rgg.ggrgg.r';
      for (let i = 0; i < 28; i += 1) {
        const k = pattern[i % pattern.length];
        const fill = k === 'g' ? G : k === 'r' ? R : F;
        cells.push(<rect key={i} x={4 + (i % 7) * 13.4} y={6 + Math.floor(i / 7) * 13.5} width="11.4" height="11.5" rx="2" fill={fill} opacity={k === '.' ? 1 : 0.35 + ((i * 7) % 5) * 0.12} />);
      }
      return <svg viewBox="0 0 100 60" preserveAspectRatio="none">{cells}</svg>;
    }
    case 'myTrades':
    case 'myPositions':
    case 'mkMovers':
    case 'myLiveAccounts':
    case 'twAccounts':
      return (
        <svg viewBox="0 0 100 60" preserveAspectRatio="none">
          {[0, 1, 2, 3, 4].map((i) => (
            <g key={i}>
              {type === 'myLiveAccounts' || type === 'twAccounts' ? <circle cx="8" cy={9 + i * 11} r="2.6" fill={i === 3 ? R : G} /> : <rect x="4" y={6 + i * 11} width="7" height="7" rx="2" fill={L} />}
              <rect x="15" y={7 + i * 11} width={34 - (i % 3) * 6} height="4.5" rx="2" fill={L} />
              <rect x={78 - (i % 2) * 6} y={7 + i * 11} width={18 + (i % 2) * 6} height="4.5" rx="2" fill={i % 3 === 1 ? R : G} opacity="0.75" />
            </g>
          ))}
        </svg>
      );
    case 'myAllocation':
      return (
        <svg viewBox="0 0 100 60" preserveAspectRatio="xMidYMid meet">
          <circle cx="26" cy="30" r="17" fill="none" stroke={A} strokeWidth="8" strokeDasharray="55 107" transform="rotate(-90 26 30)" />
          <circle cx="26" cy="30" r="17" fill="none" stroke={G} strokeWidth="8" strokeDasharray="30 107" strokeDashoffset="-56" transform="rotate(-90 26 30)" />
          <circle cx="26" cy="30" r="17" fill="none" stroke="#f59e0b" strokeWidth="8" strokeDasharray="20 107" strokeDashoffset="-87" transform="rotate(-90 26 30)" />
          {[0, 1, 2].map((i) => (
            <g key={i}>
              <rect x="54" y={16 + i * 11} width="6" height="6" rx="1.5" fill={[A, G, '#f59e0b'][i]} />
              <rect x="63" y={17 + i * 11} width={24 - i * 4} height="4" rx="2" fill={L} />
            </g>
          ))}
        </svg>
      );
    case 'mkTickerTape':
      return (
        <svg viewBox="0 0 100 20" preserveAspectRatio="none">
          {[0, 1, 2, 3, 4].map((i) => (
            <g key={i}>
              <rect x={3 + i * 20} y="7" width="9" height="5" rx="2" fill={L} />
              <rect x={13 + i * 20} y="7" width="6" height="5" rx="2" fill={i % 2 ? R : G} opacity="0.8" />
            </g>
          ))}
        </svg>
      );
    case 'mkChart':
      return (
        <svg viewBox="0 0 100 60" preserveAspectRatio="none">
          {[...Array(12)].map((_, i) => {
            const up = (i * 5) % 3 !== 0;
            const mid = 40 - i * 2 + ((i * 7) % 5) * 2;
            return (
              <g key={i}>
                <line x1={6 + i * 8} x2={6 + i * 8} y1={mid - 10} y2={mid + 8} stroke={up ? G : R} strokeWidth="1" />
                <rect x={3.5 + i * 8} y={mid - 6} width="5" height="10" fill={up ? G : R} opacity="0.85" />
              </g>
            );
          })}
        </svg>
      );
    case 'mkHeatmap':
      return (
        <svg viewBox="0 0 100 60" preserveAspectRatio="none">
          {[[2, 2, 40, 34, G], [44, 2, 28, 20, R], [74, 2, 24, 20, G], [44, 24, 54, 12, G], [2, 38, 26, 20, R], [30, 38, 34, 20, G], [66, 38, 32, 20, R]].map(([x, y, w, h, c], i) => (
            // eslint-disable-next-line react/no-array-index-key
            <rect key={i} x={x} y={y} width={w} height={h} rx="2" fill={c} opacity={0.35 + (i % 3) * 0.2} />
          ))}
        </svg>
      );
    case 'mkNews':
    case 'mkCalendar':
    default:
      return (
        <svg viewBox="0 0 100 60" preserveAspectRatio="none">
          {[0, 1, 2, 3].map((i) => (
            <g key={i}>
              {type === 'mkCalendar' ? <rect x="4" y={6 + i * 13} width="10" height="5" rx="2" fill={A} opacity="0.7" /> : null}
              <rect x={type === 'mkCalendar' ? 18 : 4} y={6 + i * 13} width={60 - (i % 2) * 14} height="4.5" rx="2" fill={L} />
              <rect x={type === 'mkCalendar' ? 18 : 4} y={12 + i * 13} width={38 - (i % 3) * 6} height="3.5" rx="2" fill={F} />
            </g>
          ))}
        </svg>
      );
  }
}

export default function TemplatePreview({ templateId }) {
  const built = useMemo(() => (templateId ? buildDashboardFromTemplate(templateId) : null), [templateId]);

  if (!built) {
    // Start blank: an empty grid with a plus.
    return (
      <div className="tpl-preview tpl-preview-blank" aria-hidden="true">
        <span className="tpl-preview-plus">+</span>
      </div>
    );
  }

  const rows = Math.max(...built.layout.order.map((id) => {
    const r = built.layout.sizes[id];
    return r.row + r.h - 1;
  }));
  return (
    <div className="tpl-preview" style={{ gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`, '--tpl-rows': rows }} aria-hidden="true">
      {built.widgets.map((w, i) => {
        const r = built.layout.sizes[w.id];
        return (
          <div
            key={w.id}
            className={`tpl-preview-w tpl-w-${w.type}`}
            style={{ gridColumn: `${r.col} / span ${r.w}`, gridRow: `${r.row} / span ${r.h}`, '--i': i }}
          >
            <Glyph type={w.type} />
          </div>
        );
      })}
    </div>
  );
}

/** "4 KPIs · Equity · Calendar · Trades" */
const SHORT_NAMES = {
  myKpi: 'KPI', myEquity: 'Equity', myPnlCalendar: 'P&L calendar', myTrades: 'Trades', myPositions: 'Positions',
  myAllocation: 'Allocation', myLiveAccounts: 'Live accounts', myVsStrategy: 'Vs strategy',
  twToday: 'Trade Watcher today', twDailyPnl: 'Daily P&L', twAccounts: 'NinjaTrader accounts', twCalendar: 'TW calendar', twAlerts: 'Alerts',
  mkTickerTape: 'Ticker tape', mkChart: 'Live chart', mkHeatmap: 'Heatmap', mkMovers: 'Top movers',
  mkNews: 'News', mkCalendar: 'Economic calendar', mkSymbol: 'Price card', mkTechnicals: 'Technicals',
};

export function templateWidgetSummary(template) {
  const counts = new Map();
  template.widgets.forEach((w) => counts.set(w.type, (counts.get(w.type) || 0) + 1));
  return [...counts.entries()].map(([type, n]) => {
    const name = SHORT_NAMES[type] || type;
    return n > 1 ? `${n} ${name}s` : name;
  });
}
